import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import { useToast } from 'vue-toastification'

class HttpClient {
  private client: AxiosInstance
  private toast = useToast()

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        const loadingStore = useLoadingStore()
        
        // JWT 토큰 추가
        if (authStore.token && !config.url?.includes('/login') && !config.url?.includes('/register')) {
          config.headers.Authorization = `Bearer ${authStore.token}`
        }
        
        // 로딩 시작
        loadingStore.startLoading()
        
        return config
      },
      (error) => {
        const loadingStore = useLoadingStore()
        loadingStore.stopLoading()
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        const loadingStore = useLoadingStore()
        loadingStore.stopLoading()
        return response
      },
      (error) => {
        const loadingStore = useLoadingStore()
        const authStore = useAuthStore()
        
        loadingStore.stopLoading()

        if (error.response?.status === 401) {
          if (!error.config?.url?.includes('/login')  && !error.config.url?.includes('/register')) {
              // 토큰이 만료되었거나 유효하지 않음
              authStore.logout()
              window.location.href = '/login'
          }
        } else if (error.response?.status === 403) {
          this.toast.error('접근 권한이 없습니다.')
        } else if (error.response?.status >= 500) {
          this.toast.error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
        } else if (error.response?.data?.message) {
          this.toast.error(error.response.data.message)
        } else {
          this.toast.error('네트워크 오류가 발생했습니다.')
        }

        return Promise.reject(error)
      }
    )
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config)
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config)
  }
}

export const httpClient = new HttpClient(
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
)